import {
	AlertOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	MedicineBoxOutlined,
	ReloadOutlined,
	RollbackOutlined,
	StopOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import type { ReportKind } from "@skud/domain";
import { Link } from "@tanstack/react-router";
import { Alert, Button, Card, Col, DatePicker, Row, Segmented, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import { AttendanceRateHero } from "../components/AttendanceRateHero";
import { DepartmentAttendanceCard } from "../components/DepartmentAttendanceCard";
import { LatenessHistogram } from "../components/LatenessHistogram";
import type { DashboardRang } from "../components/palette";
import { RiskyEmployeeList } from "../components/RiskyEmployeeList";
import { SplitBar } from "../components/SplitBar";
import { StatCard } from "../components/StatCard";
import {
	useAcknowledgeRisk,
	useAttendanceTrend,
	useDashboard,
	useRefreshedAgo,
	useRiskyEmployees,
} from "../hooks/useDashboard";
import type { DashboardPayload } from "../types";
import { davomatDarajasi, foizMatn } from "../utils/davomatDarajasi";
import { ertaIzohi, kechikishIzohi, uzrsizIzohi } from "../utils/kechikishIzohi";

const { Title, Text } = Typography;

type Rejim = "bugun" | "kecha" | "sana";

interface Karta {
	title: string;
	value: number | undefined;
	icon: ReactNode;
	warning?: boolean;
	hint?: string;
	/**
	 * Tegishli hisobot turi. ⚠️ Faqat MA'NOSI aniq mos keladigan
	 * kartalarda — «yaqin» hisobotni taxmin qilish rahbarni boshqa
	 * raqamlar turgan sahifaga olib borardi.
	 *
	 * ⚠️ Tip `@skud/domain` dan (`ReportKind`), `string` EMAS: xato
	 * yozilgan tur (`"latness"`) `string` bilan typecheck'dan jimgina
	 * o'tib, faqat runtime'da bo'sh hisobot berardi.
	 */
	reportKind?: ReportKind;
	/**
	 * Ikonka rangi — diagrammalar (bo'lim barlari, kechikish gistogrammasi,
	 * hudud chizig'i) bilan BITTA `DASHBOARD_RANGLARI` palitrasidan
	 * (dizayn kanvasi, «Yangi taklif» artboardi). Ogohlantiruvchi
	 * kartalarda HISOBLANADI (faqat qiymat > 0 da `kelmagan`), doimiy
	 * plitkalarda esa qat'iy: Kelgan→ko'k, Kechikkan→binafsha,
	 * Kelmagan→qizil, Erta ketgan→neytral.
	 */
	tone: DashboardRang;
}

/**
 * Real-vaqt boshqaruv paneli (TT §4.15, spec §7.1), dizayn kanvasining
 * «Boshqaruv paneli» artboardi tartibida.
 *
 * ⚠️⚠️ **POLLING (20 s), SSE EMAS** (kontrakt §7, spec R1) — interval
 * `useDashboard` da va u FAQAT sanasiz rejimda ishlaydi.
 *
 * ⚠️⚠️ **XARITA YO'Q** (kontrakt §7, spec R1). Leaflet bu sahifaga
 * import QILINMAYDI: u faqat hudud sahifasida lazy bo'lakda yuklanadi
 * (+154 KB gzip, CLAUDE.md «Frontend tuzilishi»).
 *
 * ⚠️⚠️ **DIAGRAMMA KUTUBXONASI YO'Q.** Sparkline, bar ro'yxati,
 * split-bar va kechikish gistogrammasi — sof SVG/CSS.
 * `@ant-design/plots` ~200 KB gzip qo'shardi va hech qanday yangi
 * imkoniyat bermasdi; `DashboardPage` esa `routeTree.tsx` da STATIK
 * import bo'lib qoladi (u login'dan keyingi qo'nish sahifasi va
 * `lazyRouteComponent` unga har kirishda spinner qo'shardi).
 * Qorovul: `dashboard-bundle.test.ts`.
 *
 * ⚠️ **Karta havolasi — `/reports/$kind?dateFrom=<sana>&dateTo=<sana>`**
 * (hisobotlar spec §2.7). Ilgari `/reports?kind=…` edi, lekin ro'yxat
 * sahifasi `kind` ni umuman o'qimasdi — foydalanuvchi kartani bosib
 * hisobotni qaytadan qidirardi. Sana — JAVOBDAGI `date` (ko'rsatilgan
 * biznes-kun), brauzer sanasi EMAS. `DashboardPage.test.tsx` pinlaydi.
 */
export function DashboardPage() {
	const { t } = useTranslation("dashboard");
	// ⚠️ Boshlang'ich sana YO'Q — birinchi so'rov sanasiz ketadi va
	// «bugun» ni SOZLANGAN zonada server hal qiladi (`useDashboard`
	// izohiga qarang). `DatePicker` javobdagi `date` bilan urug'lanadi.
	const [rejim, setRejim] = useState<Rejim>("bugun");
	const [tanlangan, setTanlangan] = useState<Dayjs | null>(null);

	/**
	 * Serverning «bugun» i — SOZLANGAN zonada hisoblangan biznes-kun.
	 *
	 * ⚠️ Brauzer sanasidan (`dayjs()`) olinmaydi: boshqa zonadagi
	 * foydalanuvchi (VPN, UTC kiosk) jimgina boshqa biznes-kunni
	 * ko'rardi. Qiymat FAQAT sanasiz so'rovdan (`tanlangan === null`)
	 * olinadi va keyingi tanlovlar uni o'zgartirmaydi.
	 */
	const [bugungiSana, setBugungiSana] = useState<string | null>(null);

	// ⚠️ «Bugun» rejimida so'rov SANASIZ ketadi. `tanlangan` ni u yerda
	// ham uzatish server hisoblagan biznes-kunni brauzer sanasiga
	// almashtirardi.
	const kun = rejim === "bugun" ? undefined : tanlangan?.format("YYYY-MM-DD");

	const { can } = usePermission();
	const canReadAttendance = can("attendance", "read");
	const canReadReports = can("reports", "read");
	const canReadEmployees = can("employees", "read");
	const canAcknowledge = can("employees", "risk_acknowledge");

	const dashboard = useDashboard(kun);
	const trend = useAttendanceTrend(kun);
	const risky = useRiskyEmployees();
	const acknowledge = useAcknowledgeRisk();

	const sanoq: DashboardPayload | undefined = dashboard.data;

	// ⚠️ Sanasiz so'rovning javobi — serverning «bugun» i. Faqat SHU
	// holatda yoziladi; tanlangan sana bilan kelgan javob uni siljitsa,
	// «Kecha» yana ko'rinishga bog'lanib qolardi.
	useEffect(() => {
		if (tanlangan === null && sanoq) {
			setBugungiSana(sanoq.date);
		}
	}, [tanlangan, sanoq]);
	const yangilandi = useRefreshedAgo(dashboard.dataUpdatedAt);

	/**
	 * ⚠️⚠️ «Kecha» JAVOBDAGI `date` dan hisoblanadi,
	 * `dayjs().subtract(1, "day")` dan EMAS: brauzer zonasidan sana
	 * qurish boshqa zonadagi foydalanuvchini (VPN, UTC kiosk) jimgina
	 * boshqa biznes-kunga olib tushardi.
	 *
	 * ⚠️ Sana BIR MARTA hisoblanib holatga yoziladi. `kun` ni har
	 * render'da `sanoq.date` dan chiqarish TSIKL berardi: yangi kalitda
	 * `sanoq` `undefined` bo'lib, `kun` ham `undefined` ga qaytardi.
	 */
	const rejimniOzgartir = (yangi: Rejim) => {
		if (yangi === "bugun") {
			setTanlangan(null);
			setRejim("bugun");

			return;
		}

		if (yangi === "kecha") {
			// ⚠️⚠️ Manba — BUGUNGI javobning sanasi (`bugungiSana`), joriy
			// ko'rinishning `sanoq.date` i EMAS. Ilgari shu yerda
			// `dayjs(sanoq.date).subtract(1, "day")` turardi: «Sana
			// tanlash» bilan 10-yanvarga o'tgan foydalanuvchi keyin
			// «Kecha» ni bosganda 9-yanvarga tushardi — tugma o'z nomini
			// yolg'on qilardi, Segmented esa «Kecha» ni tanlangan
			// ko'rsatgani uchun holat izchil ko'rinardi.
			if (!bugungiSana) {
				return;
			}

			setTanlangan(dayjs(bugungiSana).subtract(1, "day"));
			setRejim("kecha");

			return;
		}

		setRejim("sana");
	};

	const foiz = sanoq ? davomatDarajasi(sanoq.boshlangan, sanoq.plannedShifts) : null;

	const plitkalar: Karta[] = [
		{
			title: t("page.cards.kelgan"),
			/*
			 * ⚠️⚠️ `boshlangan`, `kelgan` EMAS. `status = 'kelgan'` —
			 * check-out'dan keyingi TERMINAL holat, ya'ni plitka butun ish
			 * kuni davomida ~0 ko'rsatib, kechqurun o'z-o'zidan «tuzalardi»
			 * va hech qanday test qizil bermasdi. Terminal `kelgan`
			 * yo'qolmaydi — izohga ko'chdi.
			 */
			value: sanoq?.boshlangan,
			icon: <CheckCircleOutlined />,
			hint: sanoq
				? t("page.hints.kelgan", { percent: foizMatn(foiz), value: sanoq.kelgan })
				: undefined,
			reportKind: "attendance_daily",
			tone: "kelgan",
		},
		{
			title: t("page.cards.kechikkan"),
			value: sanoq?.kechikkan,
			icon: <ClockCircleOutlined />,
			/*
			 * ⚠️ Izoh SOF FUNKSIYADAN, satr ichida shablon YOZILMAYDI —
			 * aks holda `null` qoidasi uch joyda takrorlanib, bir joyda
			 * unutilardi.
			 *
			 * ⚠️ `sanoq` hali yo'q bo'lganda izoh UMUMAN berilmaydi:
			 * `sanoq?.kechikkan ?? 0` yozilsa yuklanish paytida «Bugun
			 * kechikish yo'q» chiqib, keyin raqamga sakrardi.
			 */
			hint: sanoq
				? kechikishIzohi(sanoq.kechikkan, sanoq.avgLateMin, sanoq.maxLateMin, t)
				: undefined,
			reportKind: "lateness",
			tone: "kechikkan",
		},
		{
			title: t("page.cards.kelmagan"),
			value: sanoq?.kelmagan,
			icon: <StopOutlined />,
			hint: sanoq ? uzrsizIzohi(sanoq.kelmagan, sanoq.kelmaganUzrsiz, t) : undefined,
			reportKind: "attendance_daily",
			tone: "kelmagan",
		},
		{
			title: t("page.cards.ertaKetgan"),
			value: sanoq?.ertaKetgan,
			icon: <RollbackOutlined />,
			hint: sanoq ? ertaIzohi(sanoq.ertaKetgan, sanoq.avgEarlyMin, t) : undefined,
			tone: "neytral",
		},
	];

	/**
	 * ⚠️ Ogohlantiruvchi ikonka rangi HISOBLANADI: qiymat noldan katta
	 * bo'lganda `kelmagan` (qizil), aks holda `neytral`. Xuddi `StatCard`
	 * ning matn rangi qoidasi kabi (`qizil = warning && sanoq > 0`) —
	 * doimiy qizil ikonka «hammasi yomon» degan yolg'on taassurot berardi.
	 */
	const ogohlikRangi = (qiymat: number | undefined): DashboardRang =>
		qiymat && qiymat > 0 ? "kelmagan" : "neytral";

	const ogohlik: Karta[] = [
		{
			title: t("page.cards.uzrli"),
			value: sanoq?.uzrli,
			icon: <MedicineBoxOutlined />,
			// ⚠️ Doimiy neytral — uzrli ta'til/betoblik ALARMASH EMAS,
			// faqat ma'lumot uchun ko'rsatiladi (`warning` belgisi yo'q).
			tone: "neytral",
		},
		{
			title: t("page.cards.fakeLocation"),
			value: sanoq?.fakeLocation,
			icon: <AlertOutlined />,
			warning: true,
			/*
			 * ⚠️ HODISALAR soni, XODIMLAR soni EMAS. Kanvasdagi «6 hodisa,
			 * 4 xodimda» ikkinchi raqamni talab qiladi;
			 * `count(DISTINCT employee_id)` kontraktda YO'Q va ONGLI
			 * chiqarilgan — «4 xodimda» YOZILMAYDI.
			 */
			hint: sanoq ? t("page.hints.fakeLocation", { value: sanoq.fakeLocation }) : undefined,
			reportKind: "fake_location",
			tone: ogohlikRangi(sanoq?.fakeLocation),
		},
		{
			title: t("page.cards.xavfli"),
			value: sanoq?.xavfli,
			icon: <WarningOutlined />,
			warning: true,
			hint: sanoq ? t("page.hints.xavfli", { value: sanoq.xavfliKorilmagan }) : undefined,
			tone: ogohlikRangi(sanoq?.xavfli),
		},
	];

	const chiz = (karta: Karta) => {
		const kartaNode = (
			<StatCard
				title={karta.title}
				value={karta.value}
				icon={karta.icon}
				loading={dashboard.isLoading}
				warning={karta.warning}
				hint={karta.hint}
				clickable={Boolean(karta.reportKind) && canReadReports}
				tone={karta.tone}
			/>
		);

		// ⚠️ Havola FAQAT `reports:read` bo'lganda quriladi — ruxsatsiz
		// foydalanuvchida karta bosilmaydigan bo'lishi kerak, aks holda u
		// har bosishda qorovul rad etgan sahifaga tushardi. Sahifaga
		// qo'shiladigan HAR QANDAY yangi havola shu qoidaga bo'ysunadi:
		// shartsiz `<a>` «`attendance:read` da nol havola» testini qizil
		// qilardi.
		// ⚠️ Sana hali noma'lum (birinchi javob kelmagan) bo'lsa `search`
		// berilmaydi — hisobot sahifasi o'z standart oralig'ini oladi.
		const sana = sanoq?.date;

		return karta.reportKind && canReadReports ? (
			<Link
				to="/reports/$kind"
				params={{ kind: karta.reportKind }}
				search={sana ? { dateFrom: sana, dateTo: sana } : undefined}
				className="block"
			>
				{kartaNode}
			</Link>
		) : (
			kartaNode
		);
	};

	return (
		<div>
			<div className="mb-4 flex flex-wrap items-start justify-between gap-4">
				<div>
					<Title level={3} className="!mb-1">
						{t("page.title")}
					</Title>
					<Text type="secondary">{t("page.subtitle")}</Text>

					{/*
						⚠️ Biznes-kun JAVOBDAN, brauzer sanasidan EMAS; matn esa
						«server zonasida» EMAS: sana `settings.timezone` bo'yicha
						hisoblanadi (`attendance.repository.ts` ning
						`businessDateInZone()` i), server OS zonasi bo'yicha emas —
						«server zonasida» yozuvi foydalanuvchiga yolg'on gapirardi.

						⚠️ Zona NOMI ko'rsatilmaydi: u `GET /settings` dan kelardi va
						`useSettingsList()` da `enabled` darvozasi yo'q, ya'ni
						`settings:read` siz rahbar har render'da 403 olardi.
					*/}
					{sanoq ? (
						<Text type="secondary" className="block text-xs">
							{t("page.businessDate", { date: dayjs(sanoq.date).format("DD.MM.YYYY") })}
						</Text>
					) : null}
				</div>

				{/*
					⚠️ Boshqaruv asboblari (holat matni, rejim, sana, yangilash)
					BITTA guruhlangan panelga yig'ilgan — ilgari `Space wrap`
					ularni bo'shashgan qatorda tarqoq ko'rsatardi (dizayn
					kanvasi, «Yangi taklif» artboardi). Ichidagi elementlar va
					ular chaqiradigan funksiyalar o'zgarmagan — faqat konteyner.
				*/}
				<div className="flex flex-wrap items-center gap-2.5 rounded-[10px] border border-[#eeefec] bg-[#fafaf9] px-2.5 py-1.5 dark:border-[#22262b] dark:bg-[#1d2126]">
					{/*
						⚠️ Matn oxirgi MUVAFFAQIYATLI javobdan hisoblanadi, poll
						konstantasidan EMAS — poll yiqilganda hisoblagich o'sishi
						kerak, aynan shunda u ogohlantirishga aylanadi.
					*/}
					<Text type="secondary" className="whitespace-nowrap text-xs">
						{yangilandi}
					</Text>

					<div className="h-5 w-px bg-[#e5e6e3] dark:bg-[#2a2e33]" />

					<Segmented<Rejim>
						size="small"
						value={rejim}
						onChange={rejimniOzgartir}
						options={[
							{ label: t("page.modes.today"), value: "bugun" },
							// ⚠️ Sanoq hali yo'q bo'lsa «Kecha» ni hisoblab bo'lmaydi
							// (u JAVOBDAGI sanaga tayanadi) — tugma o'chirilgan.
							{ label: t("page.modes.yesterday"), value: "kecha", disabled: !sanoq },
							{ label: t("page.modes.pickDate"), value: "sana" },
						]}
					/>

					{/*
						⚠️⚠️ `DatePicker` DOIM DOM da va boshqa rejimlarda
						`disabled` (`hidden` EMAS — `[hidden]` bilan ham
						`querySelector` topadi, lekin `disabled` niyatni aniqroq
						bildiradi). `DashboardPage.test.tsx` standart holatda uning
						javobdagi `date` bilan urug'langanini PINLAYDI; tabiiy
						implementatsiyada u «Sana tanlash» rejimidagina chizilib,
						test qizil bo'lardi.
					*/}
					<DatePicker
						disabled={rejim !== "sana"}
						value={tanlangan ?? (sanoq ? dayjs(sanoq.date) : null)}
						allowClear={false}
						onChange={(value) => {
							if (value) {
								setTanlangan(value);
							}
						}}
					/>

					{/*
						⚠️ `refetch()`, `invalidateQueries` EMAS: ikkinchisi trendni
						ham bekor qilib, uning 10 daqiqalik `staleTime` ini ma'nosiz
						qilardi.
					*/}
					<Button
						size="small"
						icon={<ReloadOutlined />}
						loading={dashboard.isFetching}
						onClick={() => {
							// ⚠️ `void` operatori Biome'da taqiqlangan (`noVoid`) —
							// blok tanasi promise'ni baribir yutadi va `onClick`
							// `void` qaytaradi.
							dashboard.refetch();
						}}
					>
						{t("page.refreshButton")}
					</Button>
				</div>
			</div>

			{canReadAttendance ? (
				<>
					{/*
						⚠️ Yiqilgan so'rov JIM QOLMAYDI. `StatCard` `value ?? 0`
						ko'rsatadi, ya'ni birinchi so'rov yiqilsa kartalar «0»
						beradi va bu mukammal kundan farqlanmasdi. Fon poll'i
						yiqilganda esa TanStack v5 oxirgi muvaffaqiyatli `data` ni
						saqlab qoladi — raqamlar muzlab, joriy ko'rinardi. Ikki
						holat ikki xil matn oladi.
					*/}
					{dashboard.isError ? (
						<Alert
							className="mb-4"
							type="error"
							showIcon
							title={t("page.alerts.loadErrorTitle")}
							description={
								sanoq ? t("page.alerts.loadErrorWithData") : t("page.alerts.loadErrorNoData")
							}
						/>
					) : null}

					<Row gutter={[16, 16]} className="mb-4">
						<Col xs={24} xl={12}>
							{/*
								⚠️ Trend so'rovining yiqilishi ALOHIDA holat — sanoqlar
								`Alert` i uni QOPLAMAYDI. Hero raqami qoladi, sparkline
								o'rnida kichik izoh chiqadi.
							*/}
							<AttendanceRateHero
								sanoq={sanoq}
								trend={trend.data}
								trendError={trend.isError}
								kun={kun}
							/>
						</Col>

						<Col xs={24} xl={12}>
							<Row gutter={[16, 16]}>
								{plitkalar.map((karta) => (
									<Col xs={24} sm={12} key={karta.title}>
										{chiz(karta)}
									</Col>
								))}
							</Row>
						</Col>
					</Row>

					{/*
						⚠️ Ogohlantirishlar (uzrli, fake location, xavfli xodim)
						endi ALOHIDA gorizontal qatorda — ilgari xavfli xodimlar
						jadvali yonidagi tor ustunga siqilgan edi (dizayn kanvasi,
						«Yangi taklif» artboardi: bular yonma-yon turishi kerak
						bo'lgan uch xil ma'no, jadvalning «qolgan joyi» emas).
						Kartalarning O'ZI (qiymat, izoh, havola qoidasi) o'zgarmagan.
					*/}
					<div className="mb-4">
						<Title level={5} className="!mb-2">
							{t("page.warningsTitle")}
						</Title>

						<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
							{ogohlik.map((karta) => (
								<div key={karta.title}>{chiz(karta)}</div>
							))}
						</div>
					</div>

					<Row gutter={[16, 16]} className="mb-4">
						<Col xs={24} xl={12}>
							<DepartmentAttendanceCard
								departments={sanoq?.departments ?? []}
								loading={dashboard.isLoading}
							/>
						</Col>

						{/*
							⚠️ O'ng karta IKKI bo'limli, endi YONMA-YON (kanvasning
							«Yangi taklif» tuzilmasi): ilgari kechikish diagrammasi
							va «Hudud holati» ajratgich bilan VERTIKAL uyilgan edi —
							ikkalasini solishtirish uchun pastga aylantirish kerak
							edi. `LatenessHistogram` va `SplitBar` o'z `Card`
							larini YARATMAYDI — karta shu yerda quriladi.
						*/}
						<Col xs={24} xl={12}>
							<Card>
								<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
									<div>
										<LatenessHistogram
											paketlar={sanoq?.kechikishPaketlari}
											kechikkan={sanoq?.kechikkan ?? 0}
											loading={dashboard.isLoading}
										/>
									</div>

									<div className="border-[#eeefec] border-t pt-4 md:border-t-0 md:border-l md:pt-0 md:pl-6 dark:border-[#22262b]">
										<SplitBar
											title={t("page.zoneStatus.title")}
											subtitle={t("page.zoneStatus.subtitle", {
												value: sanoq?.davomEtmoqda ?? 0,
											})}
											chapYorliq={t("page.zoneStatus.inZone")}
											chapQiymat={sanoq?.hududda ?? 0}
											ongYorliq={t("page.zoneStatus.outside")}
											ongQiymat={sanoq?.tashqarida ?? 0}
										/>
									</div>
								</div>
							</Card>
						</Col>
					</Row>

					{/*
						⚠️ Xavfli xodimlar jadvali endi TO'LIQ kenglikda — ilgari
						`xl={14}` ustunga siqilgan edi, endi esa yonida
						«Ogohlantirishlar» ustuni yo'q (yuqoriga ko'chdi), ya'ni
						torayishning sababi qolmagan.
					*/}
					{canReadEmployees ? (
						<Row gutter={[16, 16]}>
							<Col span={24}>
								{/*
									⚠️ Xato holatida ro'yxat BO'SH ro'yxatdan farqlanishi
									SHART: `RiskyEmployeeList` bo'sh ro'yxatda `null`
									qaytaradi, ya'ni yiqilgan so'rov «bayroqlangan xodim
									yo'q» bo'lib ko'rinardi — bu sahifaning yagona xavf
									signali.
								*/}
								{risky.isError ? (
									<Alert
										className="mb-4"
										type="error"
										showIcon
										title={t("page.alerts.riskyLoadErrorTitle")}
										description={t("page.alerts.riskyLoadErrorDescription")}
									/>
								) : null}

								<RiskyEmployeeList
									items={risky.data ?? []}
									loading={risky.isLoading}
									canAcknowledge={canAcknowledge}
									isSubmitting={acknowledge.isPending}
									onAcknowledge={(id, reason, done) =>
										acknowledge.mutate({ id, reason }, { onSuccess: done })
									}
								/>
							</Col>
						</Row>
					) : null}

					{/*
						⚠️ Sanoqlar BO'LINMA emas — «Kechikkan» `late_minutes > 0`
						bo'yicha sanaladi va kechikib kelgan xodim «Kelgan» plitkasida
						HAM qoladi: u endi `boshlangan` ni ko'rsatadi va `boshlangan`
						`davom_etmoqda` bilan `kechikkan` ni ham qamraydi. Ya'ni bu
						matn plitka `kelgan` dan `boshlangan` ga o'tgandan KEYIN rost
						bo'ladi — uchala tahrir birga bajarilishi shart edi.
					*/}
					<Text type="secondary" className="mt-4 block">
						{t("page.footerNote")}
					</Text>
				</>
			) : (
				<Alert
					type="warning"
					showIcon
					title={t("page.alerts.noPermissionTitle")}
					description={t("page.alerts.noPermissionDescription")}
				/>
			)}
		</div>
	);
}
